import { useQuery } from '@apollo/client'
import { GET_SPEC_INTELLIGENCE_SECTION_STALENESS } from 'api/graphQL/queries/specIntelligence.queries'
import type {
    GetSectionInfoHeadersInput,
    GetSectionInfoHeadersResponse,
} from 'api/graphQL/types/specIntelligence.types'
import { processEnv } from 'components/configuration/environmentVariables'

const FRESHNESS_INTERVAL_MS = Math.max(
    parseInt(processEnv.REACT_APP_SpecIntStalenessInterval || '') || 300_000,
    30_000
)

/**
 * Polls the analyzed sections' `lastInvalidatedAt` timestamps at a configurable
 * interval (env: `REACT_APP_SpecIntStalenessInterval`, default 5 min) and returns
 * `isStale: true` when any section was modified after the report ran.
 *
 * Only mount this hook when results should be visible — Apollo stops polling on
 * unmount, so drawer-closed / non-results views are naturally excluded.
 */
const useSpecIntelligenceFreshness = (
    projectId: string,
    sectionNumbers: string[],
    reportRunDate: string
): { isStale: boolean } => {
    const { data } = useQuery<GetSectionInfoHeadersResponse, GetSectionInfoHeadersInput>(
        GET_SPEC_INTELLIGENCE_SECTION_STALENESS,
        {
            variables: { input: { projectId, sectionNumbers } },
            pollInterval: FRESHNESS_INTERVAL_MS,
            fetchPolicy: 'no-cache',
            skip: sectionNumbers.length === 0,
        }
    )

    const isStale = (data?.getKnowledgeBaseSectionInfoHeaders ?? []).some(
        (s) => s.lastInvalidatedAt && new Date(s.lastInvalidatedAt) > new Date(reportRunDate)
    )

    return { isStale }
}

export default useSpecIntelligenceFreshness
